import React, { useEffect, useState } from 'react';
import { get } from '../utils/apiClient';
import { getCachedData, setCachedData, isCacheStale } from '../utils/cacheUtils';
import LoadingSpinner from '../components/LoadingSpinner';
import PageHeader from '../components/PageHeader';
import Base64Image from '../components/Base64Image';
import {
  ClinicPageContainer,
  Section,
  SectionTitle,
  TextBlock,
  TwoColumn,
  ServicesGrid,
  ServiceCard,
  ServiceTitle,
  ServiceDescription,
  GalleryGrid,
  GalleryCard,
  GalleryTitle,
  GalleryDescription,
  ContactInfoBlock,
} from '../styles/ClinicPageStyles';

const CACHE_KEY = 'clinic_data';
const MAX_AGE_MS = 1000 * 60 * 60;

const ClinicPage = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      const cached = await getCachedData(CACHE_KEY);

      if (cached && !isCacheStale(cached, MAX_AGE_MS)) {
        if (!isMounted) return;
        setPageData(cached.data);
        setLoading(false);
      }

      try {
        const data = await get('/clinic');
        if (!isMounted) return;
        setPageData(data);
        setCachedData(CACHE_KEY, data);
        setLoading(false);
      } catch (err) {
        if (!isMounted) return;
        if (!cached) {
          setLoading(false);
        }
        setError(err && err.message ? err.message : 'Failed to load clinic page.');
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  if (loading && !pageData) {
    return (
      <ClinicPageContainer>
        <LoadingSpinner />
      </ClinicPageContainer>
    );
  }

  if (error && !pageData) {
    return <ClinicPageContainer>{error}</ClinicPageContainer>;
  }

  const header = pageData && pageData.header ? pageData.header : null;
  const overviewText = pageData ? pageData.overviewText : null;
  const establishmentStory = pageData ? pageData.establishmentStory : null;
  const services = pageData && Array.isArray(pageData.services) ? pageData.services : [];
  const galleryImages =
    pageData && Array.isArray(pageData.galleryImages) ? pageData.galleryImages : [];
  const challengesText = pageData ? pageData.challengesText : null;
  const contactInfo = pageData ? pageData.contactInfo : null;

  const sortedGallery = [...galleryImages].sort((a, b) => {
    const ao = typeof a.displayOrder === 'number' ? a.displayOrder : 0;
    const bo = typeof b.displayOrder === 'number' ? b.displayOrder : 0;
    return ao - bo;
  });

  return (
    <ClinicPageContainer>
      {header && (
        <PageHeader
          title={header.title}
          image={header.backgroundImage}
        />
      )}

      {(overviewText || (establishmentStory && establishmentStory.image)) && (
        <Section>
          <SectionTitle>CLINIC PROJECT:</SectionTitle>
          <TwoColumn>
            {overviewText && <TextBlock>{overviewText}</TextBlock>}
            {establishmentStory && establishmentStory.image && (
              <div>
                <Base64Image
                  image={establishmentStory.image}
                  alt="Clinic building"
                  style={{ width: '100%', maxWidth: 420, borderRadius: 8 }}
                />
              </div>
            )}
          </TwoColumn>
        </Section>
      )}

      {services.length > 0 && (
        <Section>
          <SectionTitle>Services</SectionTitle>
          <ServicesGrid>
            {services.map((service, index) => (
              <ServiceCard key={service._id || service.title || index}>
                {service.title && <ServiceTitle>{service.title}</ServiceTitle>}
                {service.description && (
                  <ServiceDescription>{service.description}</ServiceDescription>
                )}
              </ServiceCard>
            ))}
          </ServicesGrid>
        </Section>
      )}

      {sortedGallery.length > 0 && (
        <Section>
          <SectionTitle>Clinic Gallery</SectionTitle>
          <GalleryGrid>
            {sortedGallery.map((item, index) => (
              <GalleryCard key={item._id || item.title || index}>
                {item.image && (
                  <Base64Image
                    image={item.image}
                    alt={item.title || 'Clinic facility'}
                    style={{ width: '100%', borderRadius: 6, marginBottom: 6 }}
                  />
                )}
                {item.title && <GalleryTitle>{item.title}</GalleryTitle>}
                {item.description && (
                  <GalleryDescription>{item.description}</GalleryDescription>
                )}
              </GalleryCard>
            ))}
          </GalleryGrid>
        </Section>
      )}

      {challengesText && (
        <Section>
          <SectionTitle>CHALLENGES:</SectionTitle>
          <TextBlock>{challengesText}</TextBlock>
        </Section>
      )}

      {contactInfo && (contactInfo.address || contactInfo.phone || contactInfo.email) && (
        <Section>
          <SectionTitle>Contact Information</SectionTitle>
          <ContactInfoBlock>
            {contactInfo.address && (
              <p><strong>Address:</strong> {contactInfo.address}</p>
            )}
            {contactInfo.phone && (
              <p><strong>Phone:</strong> {contactInfo.phone}</p>
            )}
            {contactInfo.email && (
              <p><strong>Email:</strong> {contactInfo.email}</p>
            )}
          </ContactInfoBlock>
        </Section>
      )}
    </ClinicPageContainer>
  );
};

export default ClinicPage;
